'use client';

import React, { useEffect, useRef } from 'react';

const integrations = [
  {
    source: 'S/4HANA Finance',
    target: 'SAC Planning',
    method: 'Live Connection / Import',
    dataTypes: ['GL Actuals', 'Cost Centre Data', 'Profit Centre Data', 'Company Code Structure'],
    notes: 'Live connection for real-time actuals; import for large datasets. Consider Datasphere for transformation.',
  },
  {
    source: 'S/4HANA Sales & Distribution',
    target: 'SAC Planning',
    method: 'Datasphere → SAC',
    dataTypes: ['Sales Orders', 'Billing Data', 'Customer Master', 'Material Master'],
    notes: 'Recommended to route through Datasphere for harmonisation with financial planning dimensions.',
  },
  {
    source: 'S/4HANA Materials Management',
    target: 'SAC Planning',
    method: 'Datasphere → SAC',
    dataTypes: ['Purchase Orders', 'Inventory Levels', 'Vendor Data', 'Material Movements'],
    notes: 'Supply chain planning data typically requires significant transformation before use in planning models.',
  },
  {
    source: 'SAP Datasphere',
    target: 'SAC Planning',
    method: 'Native Integration',
    dataTypes: ['Harmonised Actuals', 'Master Data', 'Hierarchies', 'Reference Data'],
    notes: 'Primary recommended integration pattern for complex data landscapes. Provides transformation, harmonisation, and data quality management.',
  },
  {
    source: 'Non-SAP / External Systems',
    target: 'SAC Planning',
    method: 'Flat File / API',
    dataTypes: ['Workforce Data (HR)', 'Market Data', 'CRM Pipeline', 'Commodity Prices'],
    notes: 'Flat file integration via SAC import jobs. API integration via OData for automated pipelines.',
  },
];

export default function IntegrationLayers() {
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const section = sectionRef.current;
    if (!section) return;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.querySelectorAll('.reveal-on-scroll').forEach((el) => {
              (el as HTMLElement).classList.add('is-visible');
            });
          }
        });
      },
      { threshold: 0.03 }
    );
    observer.observe(section);
    return () => observer.disconnect();
  }, []);

  return (
    <section ref={sectionRef} className="py-20 px-6 border-b border-border bg-secondary">
      <div className="max-w-container mx-auto">
        <div className="mb-10 reveal-on-scroll">
          <div className="mono-label mb-3">Integration Reference</div>
          <h2
            className="font-heading font-semibold text-foreground"
            style={{ fontSize: 'clamp(1.5rem, 2.5vw, 2.25rem)', letterSpacing: '-0.025em' }}
          >
            Integration Patterns for SAC Planning
          </h2>
          <p className="mt-2 text-sm text-muted font-body max-w-xl leading-relaxed">
            Integration design is one of the most consequential architectural decisions in a SAC Planning program.
            The right pattern depends on data volume, transformation complexity, and latency requirements.
          </p>
        </div>

        <div className="flex flex-col gap-3">
          {integrations.map((intg, i) => (
            <div
              key={intg.source}
              className={`reveal-on-scroll reveal-delay-${Math.min(i + 1, 6)} bg-white border border-border p-6`}
              style={{ borderRadius: '4px' }}
            >
              <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
                {/* Source → Target */}
                <div className="md:col-span-3">
                  <div className="mono-label mb-1">Source</div>
                  <div className="font-heading font-semibold text-foreground text-sm mb-2">
                    {intg.source}
                  </div>
                  <div className="flex items-center gap-2 my-2">
                    <div className="w-4 h-px bg-border-strong" />
                    <span className="mono-label text-accent">{intg.method}</span>
                    <div className="w-4 h-px bg-border-strong" />
                    <span style={{ color: 'var(--color-accent)', fontSize: '10px' }}>▶</span>
                  </div>
                  <div className="mono-label mb-1">Target</div>
                  <div className="font-heading font-semibold text-foreground text-sm">
                    {intg.target}
                  </div>
                </div>

                {/* Data types */}
                <div className="md:col-span-4">
                  <div className="mono-label mb-2">Data Types</div>
                  <div className="flex flex-wrap gap-1.5">
                    {intg.dataTypes.map((dt) => (
                      <span key={dt} className="tag-pill">{dt}</span>
                    ))}
                  </div>
                </div>

                {/* Notes */}
                <div className="md:col-span-5">
                  <div className="mono-label mb-2">Architecture Notes</div>
                  <p className="text-sm text-muted font-body leading-relaxed">{intg.notes}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}